// Framework-agnostic widget config builder. Lives in shared constants so both the valet
// frontend (widget editor) and backend services (merchant-svc store onboarding) compile the
// exact same default widget config JSON from a single source of truth. MUST NOT import any
// Nuxt/Pinia/browser APIs — it is imported by NestJS services.
import { DEFAULT_TITLE_SPACING, DEFAULT_WIDGET_DESCRIPTION, WIDGET_CONFIGS } from './widget.configs.constants';
import { cartLogoBoxSize, DEFAULT_LOGO_SIZE, OP_CE_LOGO_SVG } from './widget-logo';
import {
  CartWidgetConfig,
  CheckoutWidgetConfig,
  TileFeature,
  Widget,
  WidgetConfigElement,
  WidgetFormValues,
  WidgetType,
  WidgetTypeValue,
  WidgetVariant,
  normalizeWidgetVariant,
  WidgetVariantValue,
} from './widget-config.types';

/**
 * The stack that holds the title row above the description; its gap is the only space between them.
 *
 * Matched against the pristine template, not the compiled output: every caller runs this on a parent
 * whose children are rewritten later in the pass, so the description is still its shipped self here
 * even where the compiler goes on to rename it or swap it for the store-credit wrapper.
 */
function isTitleTextStack(element: WidgetConfigElement): boolean {
  if (element.type !== 'vertical-stack') return false;

  return (element.children ?? []).some(
    child => child.type === 'custom-text' && child.attributes.text === DEFAULT_WIDGET_DESCRIPTION,
  );
}

// Function to recursively update CART widget config with form values
function updateCartConfigWithFormValues(
  config: WidgetConfigElement[],
  formValues: WidgetFormValues,
  firstVerticalStackFound = false,
): WidgetConfigElement[] {
  return config.map(item => {
    const updatedItem = { ...item };

    // Update custom-text elements
    if (item.type === 'custom-text') {
      const { text } = item.attributes;
      if (text === 'Order Protection' || text === 'Checkout+') {
        // Determine the appropriate default text based on the original text
        Object.assign(updatedItem.attributes, {
          color: formValues.widgetLabelFontColor,
          font: formValues.labelFontFamily.split(',')[0],
          size: `${formValues.widgetLabelFontSize}px`,
          text: formValues.widgetLabel || text,
          weight: formValues.titleTextBold ? 'bold' : 'normal',
        });
      } else if (text === DEFAULT_WIDGET_DESCRIPTION) {
        // Create horizontal-stack with two custom-text elements for description and store credit
        if (formValues.showStoreCredit && formValues.storeCreditText) {
          // Replace the single custom-text with a horizontal-stack containing two custom-text elements
          return {
            attributes: {
              style: 'display: inline-block;',
            },
            children: [
              {
                attributes: {
                  align: 'left',
                  color: formValues.descriptionFontColor || 'rgba(29, 36, 51, 0.8)',
                  font: formValues.labelFontWoff ? 'custom' : 'Inter',
                  'letter-spacing': 'normal',
                  'line-height': 'normal',
                  size: `${formValues.descriptionFontSize}px`,
                  text: formValues.widgetDescription || DEFAULT_WIDGET_DESCRIPTION,
                  'text-transform': 'none',
                  weight: '400',
                },
                type: 'custom-text',
              },
              {
                attributes: {
                  align: 'left',
                  color: 'rgba(8, 135, 93, 1)',
                  font: formValues.labelFontWoff ? 'custom' : 'Inter',
                  isStoreCredit: true,
                  'letter-spacing': 'normal',
                  'line-height': 'normal',
                  size: `${formValues.descriptionFontSize}px`,
                  storeCreditCurrency: 'USD',
                  storeCreditValue: 4,
                  text: formValues.storeCreditText,
                  'text-transform': 'none',
                  weight: '700',
                },
                listens: ['settings/loaded', 'cart/updated'],
                type: 'custom-text',
              },
            ],
            type: 'horizontal-stack',
          };
        } else {
          // Just update the text without store credit
          Object.assign(updatedItem.attributes, {
            color: formValues.descriptionFontColor,
            size: `${formValues.descriptionFontSize}px`,
            text: formValues.widgetDescription || DEFAULT_WIDGET_DESCRIPTION,
          });
        }
      } else if (text === 'Continue without Checkout+') {
        // Swapping the link for a real button is a structural replacement, so it only happens
        // when asked for. Otherwise the custom-text is returned exactly as before.
        if (formValues.declineButtonStyle === 'button') {
          return {
            attributes: {
              text: formValues.hyperlinkText || 'Continue without Checkout+',
              action: 'remove',
              width: '100%',
              'background-color': formValues.buttonBackground || '#000000',
              color: formValues.buttonFontColor || '#ffffff',
              border: formValues.buttonBorder || 'none',
              'border-radius': formValues.buttonBorderRadiusValue || '4px',
              padding: formValues.buttonPadding || '12px 16px',
              'font-size': formValues.buttonFontSize || '14px',
              'font-family': formValues.buttonFontFamily || 'Inter',
              'text-transform': formValues.buttonTextTransform || 'uppercase',
              'text-weight': formValues.buttonTextWeight || '600',
              'letter-spacing': formValues.buttonLetterSpacing || '0.05em',
              'align-items': 'center',
              'justify-content': 'center',
              cursor: 'pointer',
            },
            // Both: emits reaches the bus, action selects the event at click time.
            emits: ['protection-remove'],
            type: 'ui-button',
          };
        }

        Object.assign(updatedItem.attributes, {
          color: formValues.descriptionFontColor,
          size: `${formValues.descriptionFontSize}px`,
          text: formValues.hyperlinkText || 'Continue without Checkout+',
        });
      }
    }

    if (item.type === 'ui-image' && item.attributes.alt === 'Checkout+') {
      // The cart draws the mark whatever the title style, so its own artwork applies either way.
      if (formValues.logoUrl?.trim()) {
        updatedItem.attributes.src = formValues.logoUrl.trim();
      }

      // Only the built-in mark carries checkout's padding; merchant artwork is drawn as drawn.
      const isBuiltInMark = updatedItem.attributes.src === OP_CE_LOGO_SVG;
      const size = cartLogoBoxSize(formValues.logoSize ?? DEFAULT_LOGO_SIZE, isBuiltInMark);

      Object.assign(updatedItem.attributes, {
        width: `${size}px`,
        height: `${size}px`,
        ...(formValues.logoColor?.trim() ? { color: formValues.logoColor.trim() } : {}),
      });

      // The box now carries the padding, so a `scale` on top of it would shrink the mark twice.
      delete updatedItem.attributes.scale;
    }

    // Handle info modal visibility
    if (item.type === 'ui-modal' && item.attributes.role === 'dialog') {
      if (!formValues.showInfoModal) {
        updatedItem.attributes.style = 'display: none;';
      }
    }

    if (item.type === 'ui-image' && item.attributes.isInfoModal === true) {
      if (formValues.showInfoModal) {
        updatedItem.attributes.src = 'https://op-media-asset.s3.us-west-1.amazonaws.com/assets/info-icon.svg';
      } else {
        // Hide the info modal by setting display to none
        updatedItem.attributes.style = 'display: none;';
      }
    }

    // Update ui-switch based on button text
    if (item.type === 'ui-switch') {
      const isCheckbox = formValues.buttonText === 'checkbox';
      updatedItem.attributes = {
        ...updatedItem.attributes,
        ...(isCheckbox
          ? {
              oncolor: formValues.buttonBackground,
              type: 'checkbox',
            }
          : {
              'aria-label': 'Toggle Checkout+ protection',
              offcolor: '#1D243359',
              oncolor: formValues.buttonBackground || '#000000',
              tabIndex: '0',
              type: 'toggle',
            }),
      };
    }

    // Update ui-button styling. Matched by type alone, so scope it to the primary CTA: the
    // decline button produced above also has this type and must not inherit its styling.
    const isPrimaryButton = item.type === 'ui-button' && (!item.emits || item.emits.includes('protection-add'));

    if (isPrimaryButton) {
      Object.assign(updatedItem.attributes, {
        'align-items': formValues.buttonAlignItems || 'center',
        'background-color': formValues.buttonBackground || '#000000',
        border: formValues.buttonBorder || 'none',
        'border-radius': formValues.buttonBorderRadiusValue || '4px',
        color: formValues.buttonFontColor || '#ffffff',
        cursor: formValues.buttonCursor || 'pointer',
        'font-family': formValues.buttonFontFamily || 'Inter',
        'font-size': formValues.buttonFontSize || '14px',
        // `icon` is a preset name or an uploaded SVG URL; empty renders none.
        // Empty icon-color inherits the button label color in the runtime component.
        icon: formValues.buttonIcon || '',
        'icon-color': formValues.buttonIconColor || '',
        'icon-divider': formValues.buttonIconDivider === false ? 'false' : 'true',
        'justify-content': formValues.buttonJustifyContent || 'center',
        'letter-spacing': formValues.buttonLetterSpacing || '0.05em',
        padding: formValues.buttonPadding || '12px 16px',
        text: formValues.buttonDisplayText || 'Checkout+',
        'text-transform': formValues.buttonTextTransform || 'uppercase',
        'text-weight': formValues.buttonTextWeight || '600',
      });

      // Row layout: put the theme's own View Cart control beside the CTA. Requires a selector —
      // without one there is nothing to project and the lone button would sit in an odd half-row.
      if (formValues.showViewCartButton && formValues.viewCartSelector) {
        return {
          attributes: {
            'align-items': 'center',
            'child-flex': '1 1 0',
            'flex-direction': formValues.checkoutPosition === 'left' ? 'row-reverse' : 'row',
            gap: formValues.ctaRowGap || '8px',
            'justify-content': 'space-between',
            padding: '0',
            width: '100%',
          },
          children: [
            {
              attributes: {
                flex: '1 1 0',
                name: formValues.viewCartSlotName || 'op-view-cart',
                'source-selector': formValues.viewCartSelector,
              },
              type: 'op-slot',
            },
            { ...updatedItem, attributes: { ...updatedItem.attributes, width: '100%' } },
          ],
          type: 'horizontal-stack',
        };
      }
    }

    // The template centres the decline link in a content-width stack. A button needs the full
    // row, so widen the wrapper — matched on its child, since this runs before recursion.
    if (
      item.type === 'horizontal-stack' &&
      formValues.declineButtonStyle === 'button' &&
      item.children?.some(
        child => typeof child.attributes?.text === 'string' && child.attributes.text.includes('Continue without'),
      )
    ) {
      Object.assign(updatedItem.attributes, {
        'align-items': 'stretch',
        'child-flex': '1 1 auto',
        width: '100%',
      });
    }

    if (isTitleTextStack(item)) {
      updatedItem.attributes.gap = `${formValues.titleSpacing ?? DEFAULT_TITLE_SPACING}px`;
    }

    // Update container attributes for margins and padding (only for first vertical-stack)
    if (item.type === 'vertical-stack' && !firstVerticalStackFound) {
      firstVerticalStackFound = true;
      const hasMargins =
        formValues.marginTop || formValues.marginRight || formValues.marginBottom || formValues.marginLeft;
      const hasPadding =
        formValues.paddingTop || formValues.paddingRight || formValues.paddingBottom || formValues.paddingLeft;

      if (hasMargins) {
        updatedItem.attributes.margin = `${formValues.marginTop}px ${formValues.marginRight}px ${formValues.marginBottom}px ${formValues.marginLeft}px`;
      }
      if (hasPadding) {
        updatedItem.attributes.padding = `${formValues.paddingTop}px ${formValues.paddingRight}px ${formValues.paddingBottom}px ${formValues.paddingLeft}px`;
      }
    }

    // Update border visibility based on showBorder setting
    if (item.type === 'bordered-container') {
      if (!formValues.showBorder) {
        Object.assign(updatedItem.attributes, {
          'border-style': 'none',
          'border-width': '0px',
        });
      } else {
        const borders = [
          formValues.borderWidthTop || 0,
          formValues.borderWidthRight || 0,
          formValues.borderWidthBottom || 0,
          formValues.borderWidthLeft || 0,
        ]
          .map(width => `${width}px`)
          .join(' ');

        Object.assign(updatedItem.attributes, {
          'border-color': formValues.borderColor || '#e6e6e6',
          'border-style': 'solid',
          'border-width': borders,
        });
      }
    }

    // Recursively update children
    if (item.children?.length) {
      updatedItem.children = updateCartConfigWithFormValues(item.children, formValues, firstVerticalStackFound);
    }

    return updatedItem;
  });
}

// Function to update Comparison Cart widget config with form values
function updateComparisonCartConfigWithFormValues(
  config: WidgetConfigElement[],
  formValues: WidgetFormValues,
  firstBorderedContainerFound = false,
  firstVerticalStackFound = false,
  parentContext?: { type: string; id?: string },
): WidgetConfigElement[] {
  return config.map(item => {
    const updatedItem = { ...item };

    // Update custom-text elements
    if (item.type === 'custom-text') {
      const { text } = item.attributes;
      if (text === 'Choose your Coverage') {
        const displayText =
          formValues.widgetLabel === 'Order Protection'
            ? 'Choose your Coverage'
            : formValues.widgetLabel || 'Choose your Coverage';
        Object.assign(updatedItem.attributes, {
          color: formValues.widgetLabelFontColor,
          font: formValues.labelFontFamily.split(',')[0],
          size: `${formValues.widgetLabelFontSize}px`,
          text: displayText,
          weight: formValues.titleTextBold ? 'bold' : 'normal',
        });
      } else if (
        text ===
        'By selecting VIP, I agree to include shipping protection and priority support to my order at checkout.'
      ) {
        // For Comparison Cart widgets, create horizontal-stack with two custom-text elements
        if (formValues.showStoreCredit && formValues.storeCreditText) {
          // Replace the single custom-text with a horizontal-stack containing two custom-text elements
          return {
            attributes: {
              style: 'display: inline-block;',
            },
            children: [
              {
                attributes: {
                  align: 'left',
                  color: formValues.descriptionFontColor || 'rgba(29, 36, 51, 0.8)',
                  font: formValues.labelFontWoff ? 'custom' : 'Inter',
                  'letter-spacing': 'normal',
                  'line-height': 'normal',
                  size: `${formValues.descriptionFontSize}px`,
                  text: formValues.showDescription ? formValues.widgetDescription || text : text,
                  'text-transform': 'none',
                  weight: '400',
                },
                type: 'custom-text',
              },
              {
                attributes: {
                  align: 'left',
                  color: 'rgba(8, 135, 93, 1)',
                  font: formValues.labelFontWoff ? 'custom' : 'Inter',
                  isStoreCredit: true,
                  'letter-spacing': 'normal',
                  'line-height': 'normal',
                  size: `${formValues.descriptionFontSize}px`,
                  text: formValues.storeCreditText.replace('<br>', ' '),
                  'text-transform': 'none',
                  weight: '700',
                },
                listens: ['settings/loaded', 'cart/updated'],
                type: 'custom-text',
              },
            ],
            type: 'horizontal-stack',
          };
        } else {
          // Just update the text without store credit
          Object.assign(updatedItem.attributes, {
            color: formValues.descriptionFontColor,
            size: `${formValues.descriptionFontSize}px`,
            text: formValues.showDescription ? formValues.widgetDescription || text : text,
          });
        }
      } else if (text === 'VIP') {
        // Update VIP label
        Object.assign(updatedItem.attributes, {
          color: formValues.widgetLabelFontColor,
          text: formValues.includedLabel || 'VIP',
        });
      } else if (text === 'Standard') {
        // Update Standard label
        Object.assign(updatedItem.attributes, {
          color: formValues.widgetLabelFontColor,
          text: formValues.excludedLabel || 'Standard',
        });
      }
    }

    // Update container attributes for margins and padding (only for first vertical-stack)
    if (item.type === 'vertical-stack' && !firstVerticalStackFound) {
      firstVerticalStackFound = true;
      const hasMargins =
        formValues.marginTop || formValues.marginRight || formValues.marginBottom || formValues.marginLeft;
      const hasPadding =
        formValues.paddingTop || formValues.paddingRight || formValues.paddingBottom || formValues.paddingLeft;

      if (hasMargins) {
        updatedItem.attributes.margin = `${formValues.marginTop}px ${formValues.marginRight}px ${formValues.marginBottom}px ${formValues.marginLeft}px`;
      }
      if (hasPadding) {
        updatedItem.attributes.padding = `${formValues.paddingTop}px ${formValues.paddingRight}px ${formValues.paddingBottom}px ${formValues.paddingLeft}px`;
      }
    }

    // Update border visibility based on showBorder setting (only for first bordered-container)
    if (item.type === 'bordered-container' && !firstBorderedContainerFound) {
      firstBorderedContainerFound = true;
      if (!formValues.showBorder) {
        Object.assign(updatedItem.attributes, {
          'border-style': 'none',
          'border-width': '0px',
        });
      } else {
        const borders = [
          formValues.borderWidthTop || 0,
          formValues.borderWidthRight || 0,
          formValues.borderWidthBottom || 0,
          formValues.borderWidthLeft || 0,
        ]
          .map(width => `${width}px`)
          .join(' ');

        Object.assign(updatedItem.attributes, {
          'border-color': formValues.borderColor || '#e6e6e6',
          'border-style': 'solid',
          'border-width': borders,
        });
      }
    }

    // Handle radio-option styling for comparison cart
    if (item.type === 'radio-option') {
      const { id } = item.attributes;
      if (id === 'VIP') {
        // Update VIP option styling - find first bordered-container child
        if (item.children && Array.isArray(item.children)) {
          const firstBorderedContainer = item.children.find(child => child.type === 'bordered-container');
          if (firstBorderedContainer) {
            Object.assign(firstBorderedContainer.attributes, {
              'selected-border-color': formValues.includedBorderColor || '#1d2433',
            });
          }
        }
      } else if (id === 'Standard') {
        // Update Standard option styling - find first bordered-container child
        if (item.children && Array.isArray(item.children)) {
          const firstBorderedContainer = item.children.find(child => child.type === 'bordered-container');
          if (firstBorderedContainer) {
            Object.assign(firstBorderedContainer.attributes, {
              'selected-border-color': formValues.excludedBorderColor || '#1d2433',
            });
          }
        }
      }
    }

    // Handle ui-image elements for check/cancel icons
    if (item.type === 'ui-image') {
      const { src } = item.attributes;
      if (typeof src === 'string' && src.includes('Check.svg')) {
        // Use parent context to determine which radio option this belongs to
        Object.assign(updatedItem.attributes, {
          color: formValues.includedCheckColor || '#08875D',
          isSvg: true,
        });
      } else if (typeof src === 'string' && src.includes('Cancel.svg')) {
        // Use parent context to determine which radio option this belongs to
        Object.assign(updatedItem.attributes, {
          color: formValues.excludedCheckColor || '#E02D3C',
          isSvg: true,
        });
      }
    }

    // Handle info modal visibility
    if (item.type === 'ui-modal' && item.attributes.role === 'dialog') {
      if (!formValues.showInfoModal) {
        updatedItem.attributes.style = 'display: none;';
      }
    }

    // Recursively update children
    if (item.children && Array.isArray(item.children)) {
      // Pass the current item's context if it has an id, otherwise pass the existing parentContext
      const newParentContext = item.attributes?.id
        ? { id: String(item.attributes.id), type: item.type }
        : parentContext;

      updatedItem.children = updateComparisonCartConfigWithFormValues(
        item.children,
        formValues,
        firstBorderedContainerFound,
        firstVerticalStackFound,
        newParentContext,
      );
    }

    // Handle list-group to add/remove list items based on includedFeatures
    if (item.type === 'list-group' && formValues.includedFeatures && Array.isArray(formValues.includedFeatures)) {
      // Get the parent radio-option context to determine if this is VIP or Standard. NOTE this is done by id (which we control) and not the text.
      const isVIP = parentContext?.id === 'VIP';
      const isStandard = parentContext?.id === 'Standard';

      if (isVIP || isStandard) {
        // Create new list items based on includedFeatures array
        const newListItems: WidgetConfigElement[] = formValues.includedFeatures.map(
          (feature, index): WidgetConfigElement => {
            // For VIP: show Check.svg for enabled features, Cancel.svg for disabled features
            // For Standard: show Check.svg only for "Defective Items" when enabled, Cancel.svg for everything else
            const isFeatureEnabled = feature.enabled;
            const isDefectiveItems = feature.text === 'Defective Items';
            const shouldShowCheck = isVIP ? isFeatureEnabled : isDefectiveItems && isFeatureEnabled;

            return {
              attributes: {
                alignItems: 'center',
                gap: '8px',
                order: index + 1,
              },
              children: [
                {
                  attributes: {
                    color: shouldShowCheck
                      ? formValues.includedCheckColor || '#08875D'
                      : formValues.excludedCheckColor || '#E02D3C',
                    height: '12px',
                    isSvg: true,
                    opacity: shouldShowCheck ? '0.8' : '0.7',
                    src: shouldShowCheck
                      ? 'https://storage.googleapis.com/cdn.orderprotection.com/Images/Check.svg'
                      : 'https://storage.googleapis.com/cdn.orderprotection.com/Images/Cancel.svg',
                    width: '12px',
                  },
                  type: 'ui-image',
                },
                {
                  attributes: {
                    color: 'rgba(29, 36, 51, 0.65)',
                    font: 'Geist',
                    'line-height': '12px',
                    size: '11px',
                    text: feature.text,
                    weight: '400',
                  },
                  type: 'custom-text',
                },
              ],
              type: 'list-item',
            };
          },
        );

        // Replace the existing children with the new list items
        updatedItem.children = newListItems;
      }
    }

    return updatedItem;
  });
}

/** Cart-like templates by variant; any other variant compiles from the toggle template. */
const CART_TEMPLATE_BY_VARIANT: Partial<Record<WidgetVariantValue, { config: unknown }>> = {
  [WidgetVariant.COMPARISON]: WIDGET_CONFIGS.ComparisonCart,
  [WidgetVariant.CHECKOUTPLUS]: WIDGET_CONFIGS.CheckoutPlus,
  [WidgetVariant.COVERAGE]: WIDGET_CONFIGS.CoverageCart,
  [WidgetVariant.INCLUDED]: WIDGET_CONFIGS.IncludedCart,
};

/** Templates whose copy lives on their own elements and is edited in the builder, not the flat form. */
const BUILDER_ONLY_VARIANTS: WidgetVariantValue[] = [WidgetVariant.COVERAGE, WidgetVariant.INCLUDED];

export function compileWidgetConfig(
  formValues: WidgetFormValues,
  widgetType: WidgetTypeValue,
  variant?: WidgetVariantValue,
): CartWidgetConfig | CheckoutWidgetConfig {
  // Stored variants arrive in either case (see normalizeWidgetVariant); comparing the raw value
  // against the enum sent an upper-case Checkout+ row down the plain toggle template on save.
  const kind = normalizeWidgetVariant(variant);

  // SUBSCRIPTION widgets only support the toggle variant. The creation flow already
  // enforces this, but compileWidgetConfig is a public utility — guard at the boundary.
  if (widgetType === WidgetType.SUBSCRIPTION && kind !== WidgetVariant.TOGGLE) {
    throw new Error(`SUBSCRIPTION widgets only support the 'toggle' variant; got '${variant}'`);
  }

  let baseConfig: WidgetConfigElement[] | CheckoutWidgetConfig;

  // SUBSCRIPTION mirrors CART behavior (toggle-only for now).
  const isCartLike = widgetType === WidgetType.CART || widgetType === WidgetType.SUBSCRIPTION;

  if (isCartLike) {
    const template = (kind && CART_TEMPLATE_BY_VARIANT[kind]) ?? WIDGET_CONFIGS.ToggleCart;
    baseConfig = template.config as unknown as WidgetConfigElement[];
  } else if (widgetType === WidgetType.CHECKOUT) {
    baseConfig = WIDGET_CONFIGS.ToggleCheckout.config as unknown as CheckoutWidgetConfig;
  } else {
    throw new Error(`Invalid widget type: ${widgetType}`);
  }

  const compiledConfig = JSON.parse(JSON.stringify(baseConfig));

  if (isCartLike) {
    if (kind && BUILDER_ONLY_VARIANTS.includes(kind)) return compiledConfig;
    if (kind === WidgetVariant.COMPARISON) return updateComparisonCartConfigWithFormValues(compiledConfig, formValues);
    return updateCartConfigWithFormValues(compiledConfig, formValues);
  } else {
    return updateCheckoutConfigWithFormValues(compiledConfig, formValues);
  }
}

export function updateCheckoutConfigWithFormValues(
  config: CheckoutWidgetConfig,
  formValues: WidgetFormValues,
): CheckoutWidgetConfig {
  // Pulled from master list of form values.
  const checkoutProperties = [
    'actionButtonType',
    'allowedIntlCountries',
    'bannerStatus',
    'borderColor',
    'borderRadius',
    'borderStyle',
    'brandFundedText',
    'customDisclaimerText',
    'customDisclaimerTextBold',
    'customDisclaimerTextColor',
    'customDisclaimerTextSize',
    'customTextBlockBackground',
    'customTextBlockBorder',
    'customTextBlockBorderColor',
    'customTextBlockBorderRadius',
    'customTextBlockBorderWidth',
    'debugMode',
    'disclaimerTextOff',
    'disclaimerTextOffSize',
    'disclaimerTextOn',
    'disclaimerTextOnSize',
    'displayType',
    'excludeIntlOrders',
    'excludeUKIP',
    'hiddenShippingMethods',
    'suppressWhenSellingPlanInCart',
    'suppressedPaymentHandles',
    'paymentMethodRemovalText',
    'infoText',
    'infoTextSize',
    'isV2',
    'logoColor',
    'logoSize',
    'logoUrl',
    'onlyCE',
    'showBorder',
    'showBrandFundedWidget',
    'showDisclaimerText',
    'showInfoModal',
    'showStoreCredit',
    'titleText',
    'titleTextBold',
    'titleTextSize',
    'borderWidth',
    'storeCreditText',
    'tileBorderRadius',
    'tileBorderStyle',
    'tileBorderWidth',
    'cartLineRemoveLink',
    'useTileLayout',
    'tileVertical',
    'reverseLayout',
    'noCoverageTitle',
    'basicTitle',
    'warrantyTitleTemplate',
    'tileHeaderTitle',
    'noCoverageFeature1',
    'noCoverageFeature2',
    'basicFeature1',
    'basicFeature2',
    'basicFeature3',
    'warrantyFeature1',
    'warrantyFeature2Template',
    'warrantyDisclaimer',
    'noCoverageFeatures',
    'basicFeatures',
    'warrantyFeatures',
    'backgroundColor',
    'hideLogo',
    'includedHeading',
    'includedDescription',
    'includedCoveredLinkLabel',
    'includedPerRateLabel',
    'showIncludedSummaryLine',
    'includedSummaryLine',
  ];

  return {
    ...config,
    ...Object.fromEntries(
      checkoutProperties
        .map(key => [key, formValues[key as keyof WidgetFormValues]])
        .filter(([, value]) => value !== undefined),
    ),
    infoText: formValues.widgetDescription || formValues.infoText,
    titleText: formValues.widgetLabel || formValues.titleText,
    // Pinned: the customizer no longer exposes isV2 and the checkout extension always reads true.
    isV2: true,
  };
}

// Helper function to convert borderWidth to string
function convertBorderWidthToString(value: string | number | null | undefined, widgetType: WidgetTypeValue): string {
  if (value === null || value === undefined) {
    return widgetType === 'CHECKOUT' ? 'thin' : '1';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number') {
    return value.toString();
  }

  return widgetType === 'CHECKOUT' ? 'thin' : '1';
}

// Helper function to generate widget name
export function generateWidgetName(
  type: WidgetTypeValue,
  variant?: WidgetVariantValue,
  existingWidgets: Pick<Widget, 'type' | 'name'>[] = [],
): string {
  const typeLabel: string =
    ({
      [WidgetType.CART]: 'Cart',
      [WidgetType.CHECKOUT]: 'Checkout',
      [WidgetType.SUBSCRIPTION]: 'Subscription',
      [WidgetType.INFO_MODAL]: 'Info Modal',
    } as Record<WidgetTypeValue, string>)[type] ?? 'Widget';
  const widgetsOfType = existingWidgets.filter(widget => widget.type === type);
  const count = widgetsOfType.length + 1;

  const baseName = `${typeLabel} #${count.toString().padStart(2, '0')}`;

  let finalName = baseName;
  let counter = count;
  while (existingWidgets.some(widget => widget.name === finalName)) {
    counter++;
    finalName = `${typeLabel} #${counter.toString().padStart(2, '0')}`;
  }

  return finalName;
}

/**
 * Build the default widget form values for a given type/variant. Framework-agnostic version of
 * valet's `createInitialWidgetFormValues` — the only frontend coupling there is the `showStoreCredit`
 * flag (sourced from the customizations Pinia store), so it is passed in as an option here.
 */
/** Variants with their own default title; anything else reads "Order Protection". */
const DEFAULT_WIDGET_LABEL_BY_VARIANT: Partial<Record<WidgetVariantValue, string>> = {
  [WidgetVariant.COMPARISON]: 'Choose your Coverage',
  [WidgetVariant.CHECKOUTPLUS]: 'Checkout+',
  [WidgetVariant.COVERAGE]: 'Coverage Options',
};

export function buildDefaultWidgetFormValues(opts?: {
  customizations?: Partial<WidgetFormValues>;
  widgetType?: WidgetTypeValue;
  variant?: WidgetVariantValue;
  showStoreCredit?: boolean;
}): WidgetFormValues {
  const customizations = opts?.customizations;
  const widgetType: WidgetTypeValue = opts?.widgetType ?? 'CART';
  const variant: WidgetVariantValue = normalizeWidgetVariant(opts?.variant) ?? WidgetVariant.TOGGLE;
  const showStoreCredit = opts?.showStoreCredit ?? false;

  // Define default values based on widget type and variant
  const defaults: WidgetFormValues = {
    actionButtonType: variant,
    allowedIntlCountries: '',
    backgroundColor: 'transparent',
    bannerStatus: 'info',
    basicFeature1: '24/7 Support',
    basicFeature2: 'Resolutions for Shipping Issues',
    basicFeature3: '45 Day Returns',
    basicFeatures: [
      { id: '1', text: '24/7 Support' },
      { id: '2', text: 'Resolutions for Shipping Issues' },
      { id: '3', text: '45 Day Returns' },
    ],
    basicTitle: 'Basic',
    borderColor: widgetType === 'CHECKOUT' ? '#1D2433' : '#e6e6e6',
    borderRadius: 'base',
    borderStyle: 'base',
    borderWidth: convertBorderWidthToString(undefined, widgetType),
    borderWidthBottom: 1,
    borderWidthLeft: 1,
    borderWidthRight: 1,
    borderWidthTop: 1,
    brandFundedText: 'Free',
    buttonAlignItems: 'center',
    buttonBackground: '#000000',
    buttonBorder: 'none',
    buttonBorderRadius: 4,
    buttonBorderRadiusValue: '4px',
    buttonCursor: 'pointer',
    buttonDisplayText: 'Checkout+',
    buttonFontColor: '#ffffff',
    buttonFontFamily: 'Inter',
    buttonFontSize: '14px',
    buttonFontStyle: 'none',
    buttonIcon: '',
    buttonIconColor: '',
    buttonIconDivider: true,
    buttonJustifyContent: 'center',
    buttonLetterSpacing: '0.05em',
    buttonLink: 'right',
    buttonPadding: '12px 16px',
    buttonText: variant,
    buttonTextTransform: 'uppercase',
    buttonTextWeight: '600',
    buttonWidth: '100%',
    // Off by default: the checkout Remove link also needs a checkout-editor
    // placement, so it is opt-in per store (ENG-5627).
    cartLineRemoveLink: false,
    customDisclaimerText: '',
    customDisclaimerTextBold: false,
    customDisclaimerTextColor: 'base',
    customDisclaimerTextSize: 'small',
    customTextBlockBackground: 'transparent',
    customTextBlockBorder: false,
    customTextBlockBorderColor: 'base',
    customTextBlockBorderRadius: 'small',
    customTextBlockBorderWidth: 'thin',
    debugMode: false,
    descriptionFontColor: 'rgba(29, 36, 51, 0.8)',
    descriptionFontSize: 12,
    disclaimerLabel: '',
    disclaimerTextOff: '',
    disclaimerTextOffSize: 'extraSmall',
    disclaimerTextOn:
      'By leaving the toggle selected, I agree to include shipping insurance for added protection and priority support. I understand this will be added to my order at checkout.',
    disclaimerTextOnSize: 'extraSmall',
    displayType: 'textBlock',
    excludeIntlOrders: false,
    excludeUKIP: false,
    excludedBorderColor: '#1D2433',
    excludedCheckColor: '#E02D3C',
    excludedLabel: 'Standard',
    hiddenShippingMethods: '',
    suppressWhenSellingPlanInCart: false,
    suppressedPaymentHandles: '',
    paymentMethodRemovalText: 'Order Protection was removed because it is not available with the selected payment method.',
    hideLogo: false,
    hyperlinkText: 'Continue without Checkout+',
    showViewCartButton: false,
    viewCartSelector: '',
    viewCartSlotName: 'op-view-cart',
    declineButtonStyle: 'link',
    checkoutPosition: 'right',
    ctaRowGap: '8px',
    iconUrl: '',
    includedBorderColor: '#1d2433',
    includedCheckColor: '#08875D',
    includedCoveredLinkLabel: 'See what’s covered',
    includedDescription:
      'Every order is covered for loss, damage and theft, with 24/7 priority support on shipping claims.',
    includedFeatures: [
      { enabled: true, id: '1', text: 'Defective Items' },
      { enabled: true, id: '2', text: 'Theft, Lost, Damaged' },
      { enabled: true, id: '3', text: 'Priority 24/7 Support' },
    ],
    includedHeading: 'Order Protection included',
    includedLabel: 'VIP',
    includedPerRateLabel: 'Order Protection included',
    includedSummaryLine: 'Order Protection included in shipping',
    infoText: DEFAULT_WIDGET_DESCRIPTION,
    infoTextSize: 'extraSmall',
    isV2: true,
    labelFontFamily: 'Inter',
    labelFontWoff: '',
    logo: '',
    logoColor: '',
    logoSize: DEFAULT_LOGO_SIZE,
    logoUrl: '',
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    marginTop: 0,
    noCoverageFeature1: 'No Resolutions for Shipping Issues',
    noCoverageFeature2: 'No Free Returns',
    noCoverageFeatures: [
      { id: '1', text: 'No Resolutions for Shipping Issues' },
      { id: '2', text: 'No Free Returns' },
    ],
    noCoverageTitle: 'No Coverage',
    onlyCE: false,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    powerUserConfig: '',
    reverseLayout: false,
    showBorder: true,
    showBrandFundedWidget: true,
    showDescription: true,
    showDisclaimerText: false,
    showIncludedSummaryLine: false,
    showInfoModal: true,
    showStoreCredit: showStoreCredit,
    storeCreditText: showStoreCredit
      ? customizations?.storeCreditText || '& receive a free {{store_credit_value}}  gift card'
      : '',
    tileBorderRadius: 'base',
    tileBorderStyle: 'base',
    tileBorderWidth: 'base',
    tileHeaderTitle: 'Choose Shipping & Support Coverage',
    tileVertical: true,
    titleSpacing: DEFAULT_TITLE_SPACING,
    titleText: 'Order Protection',
    titleTextBold: false,
    titleTextSize: 'base',
    useTileLayout: variant === WidgetVariant.TILE,
    warrantyDisclaimer:
      'By leaving Extended Coverage selected, I agree to include shipping protection, priority support, and an extended warranty to my order at checkout.',
    warrantyFeature1: 'Everything in Basic Coverage',
    warrantyFeature2Template: '{{duration}} Year Extended Warranty',
    warrantyFeatures: [
      { id: '1', text: 'Everything in Basic Coverage' },
      { id: '2', text: '{{duration}} Year Extended Warranty' },
    ],
    warrantyTitleTemplate: '{{duration}} Year Coverage',
    widgetDescription:
      variant === WidgetVariant.COMPARISON
        ? 'By selecting VIP, I agree to include shipping protection and priority support to my order at checkout.'
        : DEFAULT_WIDGET_DESCRIPTION,
    widgetLabel: DEFAULT_WIDGET_LABEL_BY_VARIANT[variant] ?? 'Order Protection',
    widgetLabelFontColor: '#1D2433',
    widgetLabelFontSize: 15,
    widgetName: '',
    widgetTitleStyle: 'label',
  };

  // Merge customizations with defaults using a simple loop
  const result = { ...defaults };

  if (customizations && typeof customizations === 'object') {
    Object.keys(customizations).forEach(key => {
      const value = (customizations as Record<string, unknown>)[key];
      if (key in result && value !== undefined && value !== null) {
        (result as Record<string, unknown>)[key] = value;
      }
    });

    // Backward compat: reconstruct arrays from fixed fields if arrays are missing
    if (
      !customizations.noCoverageFeatures &&
      (customizations.noCoverageFeature1 || customizations.noCoverageFeature2)
    ) {
      result.noCoverageFeatures = [
        { id: '1', text: customizations.noCoverageFeature1 || result.noCoverageFeature1 },
        { id: '2', text: customizations.noCoverageFeature2 || result.noCoverageFeature2 },
      ].filter((f: TileFeature) => f.text);
    }
    if (
      !customizations.basicFeatures &&
      (customizations.basicFeature1 || customizations.basicFeature2 || customizations.basicFeature3)
    ) {
      result.basicFeatures = [
        { id: '1', text: customizations.basicFeature1 || result.basicFeature1 },
        { id: '2', text: customizations.basicFeature2 || result.basicFeature2 },
        { id: '3', text: customizations.basicFeature3 || result.basicFeature3 },
      ].filter((f: TileFeature) => f.text);
    }
    if (
      !customizations.warrantyFeatures &&
      (customizations.warrantyFeature1 || customizations.warrantyFeature2Template)
    ) {
      result.warrantyFeatures = [
        { id: '1', text: customizations.warrantyFeature1 || result.warrantyFeature1 },
        { id: '2', text: customizations.warrantyFeature2Template || result.warrantyFeature2Template },
      ].filter((f: TileFeature) => f.text);
    }
  }

  // isV2 is a fixed contract with the checkout extension — the customizer no longer exposes it,
  // so a legacy stored `false` must not win the merge above.
  result.isV2 = true;

  return result;
}

/**
 * Convenience builder for onboarding/server-side seeding: produces a publish-ready default
 * widget config (compiled JSON) plus the matching customization values for a CHECKOUT or CART
 * widget using the default `toggle` variant. Used by merchant-svc to seed a fresh store.
 */
export function buildDefaultWidgetConfig(type: WidgetTypeValue): {
  config: CartWidgetConfig | CheckoutWidgetConfig;
  customizationValues: WidgetFormValues;
} {
  const variant: WidgetVariantValue = WidgetVariant.TOGGLE;
  const customizationValues = buildDefaultWidgetFormValues({ widgetType: type, variant });
  const config = compileWidgetConfig(customizationValues, type, variant);

  return { config, customizationValues };
}
